/**
 * 기술수요·오픈커뮤니티 과제 통합
 * - 공개 URL: `/demand/tech-demand-*`, `/community/open-project-*`
 * - 화면 구현: `ProjectList`, `ProjectDetail`, `ProjectRegister` 등
 * - `/projects/:portal` 는 별칭(세션 복원·직접 접근)
 */

/** @typedef {'demand'|'community'} ProjectPortal */

export const PROJECT_PORTALS = ['demand', 'community'];

/** `/projects/*` 별칭 */
export const PROJECT_PORTAL_LIST_PATH_DEMAND = '/projects/demand';
export const PROJECT_PORTAL_LIST_PATH_COMMUNITY = '/projects/community';

/** 목록·breadcrumb·내비 생성 시 사용 */
export const PROJECT_LIST_PATH_DEMAND = '/demand/tech-demand-list';
export const PROJECT_LIST_PATH_COMMUNITY = '/community/open-project-list';

/**
 * @param {string | undefined} raw
 * @returns {ProjectPortal | null}
 */
export function normalizeProjectPortal(raw) {
  const s = String(raw || '').toLowerCase();
  if (s === 'demand' || s === 'tech-demand' || s === 'tech_demand') return 'demand';
  if (s === 'community' || s === 'open-community' || s === 'open_community') return 'community';
  return PROJECT_PORTALS.includes(s) ? /** @type {ProjectPortal} */ (s) : null;
}

/** 목록 URL sessionStorage 검증용 */
export const TECH_DEMAND_LIST_PATHS = [PROJECT_LIST_PATH_DEMAND, PROJECT_PORTAL_LIST_PATH_DEMAND];
export const COMMUNITY_PROJECT_LIST_PATHS = [
  PROJECT_LIST_PATH_COMMUNITY,
  PROJECT_PORTAL_LIST_PATH_COMMUNITY,
];

/**
 * @param {string} pathname
 * @param {readonly string[]} exactPrefixes
 */
export function isProjectListPathname(pathname, exactPrefixes) {
  const p = String(pathname || '');
  return exactPrefixes.some((prefix) => p === prefix || p.startsWith(`${prefix}?`));
}

/**
 * @param {string} queryWithOrWithoutQuestion — '?a=1' 또는 'a=1'
 */
export function buildProjectListPath(portal, queryWithOrWithoutQuestion = '') {
  const base = portal === 'community' ? PROJECT_LIST_PATH_COMMUNITY : PROJECT_LIST_PATH_DEMAND;
  const q = String(queryWithOrWithoutQuestion || '').trim();
  if (!q) return base;
  const qs = q.startsWith('?') ? q : `?${q}`;
  return `${base}${qs}`;
}

/**
 * @param {ProjectPortal} portal
 * @param {string|number} id
 * @param {string} [queryWithOrWithoutQuestion]
 */
export function buildProjectDetailPath(portal, id, queryWithOrWithoutQuestion = '') {
  const base =
    portal === 'community'
      ? `/community/open-project-detail/${id}`
      : `/demand/tech-demand-detail/${id}`;
  const q = String(queryWithOrWithoutQuestion || '').trim();
  const qs = q ? (q.startsWith('?') ? q : `?${q}`) : '';
  return `${base}${qs}`;
}

export function buildProjectRegisterPath(portal, queryWithOrWithoutQuestion = '') {
  const base =
    portal === 'community' ? '/community/open-project-register' : '/demand/tech-demand-register';
  const q = String(queryWithOrWithoutQuestion || '').trim();
  if (!q) return base;
  const qs = q.startsWith('?') ? q : `?${q}`;
  return `${base}${qs}`;
}

export function buildProjectApplyPath(portal, queryWithOrWithoutQuestion = '') {
  const base = portal === 'community' ? '/community/open-project-apply' : '/demand/tech-demand-apply';
  const q = String(queryWithOrWithoutQuestion || '').trim();
  if (!q) return base;
  const qs = q.startsWith('?') ? q : `?${q}`;
  return `${base}${qs}`;
}

/** 기술수요 전용 — 지원자 관리 */
export function buildProjectApplicantsPath(id, queryWithOrWithoutQuestion = '') {
  const base = `/demand/tech-demand-applicants/${id}`;
  const q = String(queryWithOrWithoutQuestion || '').trim();
  if (!q) return base;
  const qs = q.startsWith('?') ? q : `?${q}`;
  return `${base}${qs}`;
}

/**
 * @param {string} pathname
 * @returns {ProjectPortal | null}
 */
export function inferProjectPortalFromPathname(pathname) {
  const p = String(pathname || '');
  if (p.startsWith('/projects/demand')) return 'demand';
  if (p.startsWith('/projects/community')) return 'community';
  if (p.startsWith('/demand/tech-demand')) return 'demand';
  if (p.startsWith('/community/open-project')) return 'community';
  return null;
}

/** 브레드크럼·배너 상위 라벨 */
export function getProjectPortalListLabel(portal) {
  return portal === 'community' ? '오픈 커뮤니티' : '기술 수요';
}
